import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';

// ============================================
// Types — match the partner ticket DTOs (datacentral-api-v4 modules/partner/tickets)
// ============================================

export type TicketStatus =
  | 'opening'
  | 'work-in-progress'
  | 'pending'
  | 'cancelled'
  | 'closed'
  | 'completed';

export type TicketPriority = 'low' | 'medium' | 'high';

export interface TicketMinimal {
  ticket_id: number;
  ticket_status: TicketStatus;
  ticket_priority: TicketPriority;
  ticket_content: string;
  ticket_type_id: number | null;
  ticket_product_name: string;
  service_id: number | null;
  service_name: string;
  ticket_requested_at: string;
  created_at: string;
}

export interface TicketComment {
  ticket_comment_id: number;
  author: 'customer' | 'support';
  /** Staff member's name for support replies; "" for the customer's own replies. */
  author_name: string;
  body: string;
  created_at: string;
}

export interface TicketHistoryEntry {
  action: string;
  /** Customer-facing English label for the milestone. */
  label: string;
  created_at: string;
}

export interface TicketAttachment {
  attachment_id: number;
  name: string;
  path: string;
  type: string;
  size: number;
}

export interface TicketDetail extends TicketMinimal {
  ticket_type_key: string;
  product_group_name: string;
  service_status: string;
  /** The customer's own account/company name. */
  company_name: string;
  /** "" until the ticket is completed/closed. */
  ticket_resolved_at: string;
  /** When support first responded ("" if not yet). */
  first_response_at: string;
  /** Timestamp of the latest reply ("" if none). */
  last_reply_at: string;
  /** Expected completion / SLA target ("" if none). */
  due_at: string;
  updated_at: string;
  /** Technical owner name(s), no email; "" if unassigned. */
  assignee_name: string;
  /** Creator's staff name; "" for customer-created tickets. */
  created_by_name: string;
  comments: TicketComment[];
  attachments: TicketAttachment[];
  /** Sanitized lifecycle feed (created / in progress / resolved / closed / escalated). */
  history: TicketHistoryEntry[];
}

export interface TicketTypeOption {
  ticket_type_id: number;
  ticket_type_title: string;
  ticket_type_key: string;
  ticket_type_group: string;
}

export interface ServiceOption {
  service_id: number;
  service_name: string;
  product_group_name: string;
  service_status: string;
}

export interface CreateTicketRequest {
  ticket_type_id: number;
  ticket_content: string;
  ticket_priority?: TicketPriority;
  service_id?: number;
  ticket_product_name?: string;
}

export interface ListTicketsParams {
  page?: number;
  pageSize?: number;
  status?: TicketStatus | '';
}

export interface PaginationMeta {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  page_next: number;
  page_prev: number;
}

export interface Paginated<T> {
  results: T[];
  pagination: PaginationMeta;
}

// ============================================
// Response envelope helpers
// ============================================
// The partner API wraps payloads as `{ message, result }` (single) or
// `{ message, results, pagination }` (list).
interface ObjectEnvelope<T> {
  message?: string;
  result: T;
}
interface ArrayEnvelope<T> {
  message?: string;
  results: T[];
  pagination?: PaginationMeta;
}

// ============================================
// Query keys
// ============================================

export const ticketKeys = {
  all: ['partner', 'tickets'] as const,
  list: (params: ListTicketsParams) => ['partner', 'tickets', 'list', params] as const,
  detail: (id: number) => ['partner', 'tickets', 'detail', id] as const,
  types: ['partner', 'tickets', 'types'] as const,
  services: ['partner', 'tickets', 'services'] as const,
};

// ============================================
// Query hooks
// ============================================

export function useTickets(params: ListTicketsParams) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Paginated<TicketMinimal>> => {
      const res = await dataCentralApi.get<ArrayEnvelope<TicketMinimal>>('/partner/tickets', {
        params: {
          page: params.page,
          pageSize: params.pageSize,
          status: params.status || undefined,
        },
      });
      return {
        results: res.data?.results ?? [],
        pagination:
          res.data?.pagination ??
          { total: 0, page: 1, page_size: params.pageSize ?? 20, total_pages: 1, page_next: 0, page_prev: 0 },
      };
    },
  });
}

/** Total ticket count for a status (empty = all), read from `pagination.total`. */
async function fetchTicketCount(status: TicketStatus | ''): Promise<number> {
  const res = await dataCentralApi.get<ArrayEnvelope<TicketMinimal>>('/partner/tickets', {
    params: { page: 1, pageSize: 1, status: status || undefined },
  });
  return res.data?.pagination?.total ?? res.data?.results?.length ?? 0;
}

/** Parallel headline counts for the support overview band. */
export function useTicketCounts(statuses: (TicketStatus | '')[]) {
  return useQueries({
    queries: statuses.map((s) => ({
      queryKey: ['partner', 'tickets', 'count', s] as const,
      queryFn: () => fetchTicketCount(s),
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    })),
  });
}

export function useTicket(id: number | undefined) {
  return useQuery({
    queryKey: ticketKeys.detail(id ?? 0),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<TicketDetail | null> => {
      try {
        const res = await dataCentralApi.get<ObjectEnvelope<TicketDetail>>(`/partner/tickets/${id}`);
        return res.data?.result ?? null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

export function useTicketTypes() {
  return useQuery({
    queryKey: ticketKeys.types,
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<TicketTypeOption[]> => {
      const res = await dataCentralApi.get<ArrayEnvelope<TicketTypeOption>>(
        '/partner/tickets/ticket_types',
      );
      return res.data?.results ?? [];
    },
  });
}

export function useTicketServices() {
  return useQuery({
    queryKey: ticketKeys.services,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ServiceOption[]> => {
      const res = await dataCentralApi.get<ArrayEnvelope<ServiceOption>>(
        '/partner/tickets/services',
      );
      return res.data?.results ?? [];
    },
  });
}

// ============================================
// Mutation hooks
// ============================================

export function useCreateTicket(
  options?: UseMutationOptions<TicketMinimal, unknown, CreateTicketRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTicketRequest) => {
      const res = await dataCentralApi.post<ObjectEnvelope<TicketMinimal>>('/partner/tickets', body);
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ticketKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useReplyToTicket(
  ticketId: number,
  options?: UseMutationOptions<TicketComment, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await dataCentralApi.post<ObjectEnvelope<TicketComment>>(
        `/partner/tickets/${ticketId}/comments`,
        { body },
      );
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      options?.onSuccess?.(...args);
    },
  });
}
